import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { refreshApp } from '../utils/appRefresh';

/**
 * 당겨서 새로고침 — 스크롤 컨테이너 겸 래퍼.
 *
 * 기존 `<div className="flex-1 overflow-y-auto">`를 그대로 대체하도록 설계했다.
 * 호출부는 className만 그대로 넘기면 되고, 내부 레이아웃은 건드리지 않는다
 * (인디케이터는 `sticky top-0 h-0`이라 자리를 차지하지 않으므로 `relative`도 필요 없다 —
 *  모바일 홈 탭 래퍼처럼 `absolute inset-0`인 컨테이너와 충돌하지 않게 하려는 것이 이유다).
 *
 * ⚠ 터치 리스너는 반드시 ref + addEventListener(..., { passive: false })로 직접 등록한다.
 * React 합성 이벤트의 onTouchMove는 root에 passive로 위임돼 preventDefault()가 무시되고,
 * 그러면 당기는 동안 컨테이너가 같이 스크롤돼 제스처 자체가 성립하지 않는다.
 *
 * ⚠ ChatWindow(채팅방 본문)에는 붙이지 않는다 — 위로 스크롤해 과거 메시지를 불러오는
 * 무한 스크롤·날짜 점프와 상단 당김이 충돌한다.
 */
interface PullToRefreshProps {
  /** 스크롤 컨테이너에 그대로 붙는 클래스 — 기존 `flex-1 overflow-y-auto`를 그대로 넘긴다 */
  className?: string;
  children: React.ReactNode;
  /** 기본은 앱 전체 새로고침. 화면별로 다른 갱신이 필요할 때만 넘긴다 */
  onRefresh?: () => void | Promise<void>;
}

/** 이만큼 당겼다 떼야 새로고침이 실행된다 */
const THRESHOLD = 72;
/** 인디케이터가 더 내려오지 않는 지점 */
const MAX_PULL = 96;
/** 손가락 이동량 대비 인디케이터가 따라오는 비율(고무줄 저항) */
const RESISTANCE = 0.5;
/** 이 거리 안에서는 아직 방향(세로 당김 vs 가로 스와이프)을 판정하지 않는다 */
const DIRECTION_SLOP = 6;

/**
 * 당김은 레이아웃이 아니라 입력 제스처라 useIsMobile()이 아니라 pointer: coarse로 판정한다
 * (CLAUDE.md '모바일 감지 2축 분리'). ChatListScreen·OnlineUsersPanel은 데스크톱 목록 패널과
 * 공용이므로 이 가드가 없으면 데스크톱에도 리스너가 붙는다.
 */
const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

export const PullToRefresh: React.FC<PullToRefreshProps> = ({ className = '', children, onRefresh }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // native 리스너는 한 번만 등록하고 최신 값은 ref로 읽는다 (재등록하면 진행 중인 제스처가 끊긴다)
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isCoarsePointer()) return;

    let alive = true;
    let startY = 0;
    let startX = 0;
    /** 상단에서 시작한 세로 당김으로 확정됨 */
    let tracking = false;
    /** 시작은 했지만 아직 방향을 판정하지 못함 */
    let pending = false;
    /** 현재 당긴 거리 — state는 렌더용이라 리스너 클로저에서 읽을 수 없다 */
    let pulled = 0;

    const cancel = () => {
      tracking = false;
      pending = false;
      pulled = 0;
      setPull(0);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (refreshingRef.current || e.touches.length !== 1) return;
      if (el.scrollTop > 0) return;
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      pending = true;
      tracking = false;
      pulled = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pending && !tracking) return;
      if (e.touches.length !== 1) { cancel(); return; }

      const dy = e.touches[0].clientY - startY;
      const dx = e.touches[0].clientX - startX;

      if (pending) {
        if (Math.abs(dy) < DIRECTION_SLOP && Math.abs(dx) < DIRECTION_SLOP) return;
        // 위로 스크롤이거나 가로 스와이프면 이 제스처는 우리 것이 아니다
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) { cancel(); return; }
        pending = false;
        tracking = true;
      }

      // 당기는 도중 목록이 스크롤됐거나 손가락이 위로 넘어가면 원래 스크롤에 돌려준다
      if (el.scrollTop > 0 || dy <= 0) { cancel(); return; }

      e.preventDefault();
      pulled = Math.min(dy * RESISTANCE, MAX_PULL);
      setPull(pulled);
    };

    const onTouchEnd = () => {
      const reached = tracking && pulled >= THRESHOLD;
      tracking = false;
      pending = false;
      pulled = 0;
      if (!reached) { setPull(0); return; }

      refreshingRef.current = true;
      setRefreshing(true);
      setPull(THRESHOLD); // 스피너 자리를 유지한 채 회전

      Promise.resolve(onRefreshRef.current ? onRefreshRef.current() : refreshApp())
        .catch(() => {})
        .finally(() => {
          // 기본 동작은 location.reload()라 보통 여기까지 오지 않는다.
          // 커스텀 onRefresh를 넘긴 경우에만 원래 상태로 돌아온다.
          if (!alive) return;
          refreshingRef.current = false;
          setRefreshing(false);
          setPull(0);
        });
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      alive = false;
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, []);

  const active = pull > 0 || refreshing;
  const ready = pull >= THRESHOLD;

  return (
    <div ref={scrollRef} className={className}>
      {/* sticky + h-0: 스크롤에 따라 흐르지 않으면서 레이아웃 자리도 차지하지 않는다 */}
      <div className="sticky top-0 z-20 h-0 flex justify-center pointer-events-none" aria-hidden="true">
        <div
          className="w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center"
          style={{
            // pull=0이면 컨테이너 위로 완전히 숨는다
            transform: `translateY(${pull - 44}px)`,
            opacity: Math.min(pull / THRESHOLD, 1),
            // 당기는 중에는 transition을 끈다 — 매 touchmove마다 애니메이션이 겹쳐 뭉개진다
            transition: active && !refreshing ? 'none' : 'transform 200ms ease, opacity 200ms ease',
          }}
        >
          <RefreshCw
            size={18}
            className={`${ready || refreshing ? 'text-brand-600' : 'text-slate-400'} ${refreshing ? 'animate-spin' : ''}`}
            style={refreshing ? undefined : { transform: `rotate(${(pull / MAX_PULL) * 270}deg)` }}
          />
        </div>
      </div>
      {children}
    </div>
  );
};
